import { PROMPT_HISTORY_DEPTH_BOUNDS } from '../../lib/terminalSettings';

const storageKey = (userId: number): string => `elowen.chat.promptHistory.${userId}`;
const capacity = (depth: number): number => Math.max(PROMPT_HISTORY_DEPTH_BOUNDS[0], Math.min(PROMPT_HISTORY_DEPTH_BOUNDS[1], Math.trunc(depth)));

/** Read on demand: another tab's accepted sends are visible without a second in-memory authority.
 * Storage/parse failures propagate to the caller's localized toast, never to the send transaction. */
export function readPromptHistory(storage: Storage, userId: number, depth: number): string[] {
  const raw = storage.getItem(storageKey(userId));
  if (raw === null) return [];
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed) || !parsed.every(value => typeof value === 'string')) throw new Error('Invalid prompt history');
  return parsed.slice(-capacity(depth));
}

export function appendPromptHistory(storage: Storage, userId: number, text: string, depth: number): void {
  if (!text.trim()) return;
  const entries = readPromptHistory(storage, userId, depth);
  if (entries.at(-1) !== text) entries.push(text);
  storage.setItem(storageKey(userId), JSON.stringify(entries.slice(-capacity(depth))));
}

/** One composer browsing gesture. Its snapshot cannot shift underneath the caret when another tab sends. */
export class PromptHistoryCursor {
  private entries: readonly string[] = [];
  private index = -1;
  private draft = '';
  private expected: string | null = null;

  reset(): void { this.index = -1; this.expected = null; this.entries = []; }

  navigate(key: string, input: string, start: number, end: number, entries: readonly string[]): { text: string; caret: number } | null {
    if (this.expected !== null && input !== this.expected) this.reset();
    if (start !== end) return null;
    if (key === 'ArrowUp') {
      if (this.index < 0) {
        if (input !== '' || !entries.length) return null;
        this.draft = input;
        this.entries = entries;
        this.index = entries.length;
      } else if (start !== 0) return null;
      if (this.index === 0) return null;
      this.index--;
      const text = this.entries[this.index];
      this.expected = text;
      return { text, caret: 0 };
    }
    if (key !== 'ArrowDown' || this.index < 0 || end !== input.length) return null;
    this.index++;
    const text = this.index === this.entries.length ? this.draft : this.entries[this.index];
    if (this.index === this.entries.length) this.reset();
    else this.expected = text;
    return { text, caret: text.length };
  }
}
